/**
 * Currency Formatting Utilities
 * Functions for formatting currency values and prices
 */

/**
 * Format a currency value with thousand separators
 * @param {number} value - Currency amount
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (value === null || value === undefined) return '0'
  return Number(value).toLocaleString()
}

/**
 * Format a currency value with the currency icon
 * @param {number} value - Currency amount
 * @param {boolean} iconFirst - Whether to show icon before value
 * @returns {string} Formatted string with icon
 */
export function formatCurrencyWithIcon(value, iconFirst = false) {
  const formatted = formatCurrency(value)
  return iconFirst ? `🪙 ${formatted}` : `${formatted} 🪙`
}

/**
 * Format a price range
 * @param {number} min - Minimum price
 * @param {number} max - Maximum price
 * @returns {string} Formatted price range
 */
export function formatPriceRange(min, max) {
  if (min === max) {
    return formatCurrency(min)
  }
  return `${formatCurrency(min)} - ${formatCurrency(max)}`
}

/**
 * Calculate and format a percentage fee
 * @param {number} amount - Base amount
 * @param {number} feePercent - Fee percentage (e.g., 5 for 5%)
 * @returns {Object} Fee calculation result
 */
export function calculateFee(amount, feePercent) {
  const fee = Math.ceil(amount * (feePercent / 100))
  const net = amount - fee

  return {
    gross: amount,
    fee,
    net,
    feePercent,
    formatted: {
      gross: formatCurrency(amount),
      fee: formatCurrency(fee),
      net: formatCurrency(net),
    },
  }
}

/**
 * Format a value with +/- prefix for changes
 * @param {number} value - Change value
 * @param {boolean} showPlus - Whether to show + for positive values
 * @returns {string} Formatted change string
 */
export function formatChange(value, showPlus = true) {
  const formatted = formatCurrency(Math.abs(value))
  if (value > 0 && showPlus) {
    return `+${formatted}`
  }
  if (value < 0) {
    return `-${formatted}`
  }
  return formatted
}

/**
 * Abbreviate large numbers (e.g., 1.5K, 2.3M)
 * @param {number} value - Number to abbreviate
 * @param {number} decimals - Decimal places (default: 1)
 * @returns {string} Abbreviated number
 */
export function abbreviateNumber(value, decimals = 1) {
  if (value === null || value === undefined) return '0'

  const num = Number(value)

  if (num < 1000) {
    return num.toString()
  }

  if (num < 1000000) {
    return (num / 1000).toFixed(decimals).replace(/\.0+$/, '') + 'K'
  }

  if (num < 1000000000) {
    return (num / 1000000).toFixed(decimals).replace(/\.0+$/, '') + 'M'
  }

  return (num / 1000000000).toFixed(decimals).replace(/\.0+$/, '') + 'B'
}
